import React, {useCallback, useEffect, useRef, useState, useTransition} from 'react';
import styled from 'styled-components';
import {Avatar, Checkbox, CircleLoading, Flexbox, GridBox, Input, Label, NullMsgLabel} from '../index';
import {
    checkNullArr,
    checkNullStr,
    deleteJsonKey,
    getArrLen,
    getDefStr,
    getHighlightedMatchedText,
    isBoolTrue,
    isJsonValueTrue,
    printError,
    printLog
} from "../utils/ReactUtils";


const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer !important;
  padding: .2rem .4rem;
  border-radius: .65rem;
  transition: var(--transition-md);
  background-color: ${(props) => (props.selected ? getDefStr(props.selectedBg, '#9ecaf8') : getDefStr(props.bg, '#fff'))};

  &:hover {
    background-color: ${(props) => (props.selected ? getDefStr(props.selectedHoverBg, '#7cbcff') : getDefStr(props.hoverBg, '#e1e1e1'))};
  }
`;

/**
 * @example
 * const USERS=
 * const OPTIONS_DATA=[
 *     { "name": "John Doe", "image": "https://randomuser.me/api/portraits/men/1.jpg", "email": "johndoe7543@gmail.com", "mobile": 8486825648 },
 *     { "name": "Jane Smith", "image": "https://randomuser.me/api/portraits/women/2.jpg", "email": "janesmith8408@gmail.com", "mobile": 6071855068 },
 *     { "name": "Bob Johnson", "image": "https://randomuser.me/api/portraits/men/3.jpg", "email": "bobjohnson2121@gmail.com", "mobile": 6300869151 },
 * ];
 * const [userList, setUserList] = useState(USERS);
 *  <SearchableUserGridList
 *         wrapperClassName={''}
 *         scrollerClassName={''}
 *         gridClassName={''}
 *         itemClassName={''}
 *         nameClassName={''}
 *         emailClassName={'fs-sm'}
 *         mobileClassName={'fs-sm'}
 *         height={'45vh'}
 *         minHeight={'45vh'}
 *
 *         itemSm={12}
 *         itemMd={12}
 *         itemGap={1}
 *
 *         itemNameKey={'name'}
 *         itemEmailKey={'email'}
 *         itemMobileKey={'mobile'}
 *         itemUsernameKey={'username'}
 *         itemImageKey={'image'}
 *
 *         inlineItem={true}
 *         imageSize={'35px'}
 *         itemBg={'transparent'}
 *         itemSelectedBg={'gray'}
 *         itemHoverBg={'darkgray'}
 *         itemSelectedHoverBg={'lightgray'}
 *         nameColor={'gray'}
 *         emailColor={'darkgray'}
 *         mobileColor={'darkgray'}
 *         nameSelectedColor={'black'}
 *         emailSelectedColor={'darkgray'}
 *         mobileSelectedColor={'darkgray'}
 *
 *         users={userList}
 *         showLoading={false}
 *         showSearch={true}
 *         showCounter={true}
 *         showSelectAll={true}
 *         showEmail={true}
 *         showMobile={true}
 *         showImage={false}
 *         listMsg={''}
 *
 *         onUsersSelected={(updatedList, selectedList) => {
 *             log('SearchableUserGridList.userList.onUsersSelected.updatedList:', updatedList);
 *             log('SearchableUserGridList.userList.onUsersSelected.selectedList:', selectedList);
 *         }}
 *   />
 */
const SearchableUserGridList = React.memo(({
                                               wrapperClassName = '',
                                               gridClassName = '',
                                               scrollerClassName = '',
                                               itemClassName = '',
                                               emailClassName = '',
                                               mobileClassName = '',
                                               nameClassName = '',
                                               labelClassName = '',

                                               label = '',
                                               msgLabelColor = 'gray',
                                               emailColor = 'gray',
                                               mobileColor = 'gray',
                                               nameColor = 'darkgray',
                                               nameSelectedColor = 'black',
                                               emailSelectedColor = 'darkgray',
                                               mobileSelectedColor = 'darkgray',
                                               scrollVariant = 'dark',

                                               itemSelectedBg = '',
                                               itemBg = '',
                                               itemSelectedHoverBg = '',
                                               itemHoverBg = '',
                                               inputBg = '',

                                               itemSm = 12,
                                               itemMd = 6,
                                               itemGap = 1,

                                               height = '40vh',
                                               minHeight = '',

                                               itemNameKey = 'name',
                                               itemUsernameKey = 'username',
                                               itemImageKey = 'image',
                                               itemEmailKey = 'email',
                                               itemMobileKey = 'mobile',

                                               imageSize = '35px',
                                               showNameInitials = true,
                                               inlineItem = false,
                                               showEmail = true,
                                               showMobile = false,
                                               showImage = true,

                                               showLoading = false,
                                               showSearch = true,
                                               showCounter = true,
                                               showSelectAll = true,
                                               listMsg = "",


                                               users = [],
                                               renderItem,
                                               onUsersSelected,
                                               mutateSource = true,
                                           }) => {
    const TAG = 'SearchableUserGridList';

    const M_ID = 'mId';
    const wrapperRef = useRef(null);

    const [userList, setUserList] = useState([]);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [searchByOption, setSearchByOption] = useState();

    const [dataLoadingPending, startDataLoadingTransition] = useTransition();
    const [searchPending, startSearchTransition] = useTransition();
    const [itemSelectPending, startItemSelectTransition] = useTransition();

    useEffect(() => {
        startDataLoadingTransition(() => {
            // setUserList(getUserListOrg());
            // log('useEffect.getUserListOrg():', getUserListOrg(), 'itemNameKey:', itemNameKey, 'itemEmailKey:', itemEmailKey, 'itemImageKey:', itemImageKey, 'itemMobileKey:', itemMobileKey, 'itemUsernameKey:', itemUsernameKey);
            // if (!checkNullArr(userList) && checkNullArr(getUserListOrg())) {
            if (checkNullArr(getUserListOrg())) {
                const selectedIds = new Set([]);
                const updatedList = getUserListOrg().map((item, index) => {
                    if (isJsonValueTrue(item, 'selected')) {
                        selectedIds.add(index);
                    }
                    return {
                        ...item,
                        [M_ID]: index,
                    };
                });
                setUserList(updatedList);
                setSelectedUserIds(selectedIds);
            }
        });
    }, [users, itemNameKey]);

    const handleSearchChange = useCallback((e) => {
        // const handleSearchChange = (e) => {

        const inputValue = e.target.value + '';
        setSearchInputValue(inputValue);
        startSearchTransition(() => {
            const value = e.target.value + '';
            setSearchInputValue(value);
            setUserList((prevUserList) => prevUserList.map((item) => ({
                ...item,
                searched: (item[itemNameKey]
                    + (isBoolTrue(showEmail) ? item[itemEmailKey] : '')
                    + (isBoolTrue(showMobile) ? item[itemMobileKey] : '') + '')
                    .toLowerCase().includes(value.toLowerCase())
            })));
        });
        // startSearchTransition(() => {
        //     const inputValueL = (searchInputValue + '').toLowerCase();
        //     setUserList((prevList) => prevList.map((item) => {
        //         const name = (getJsonValue(item, itemNameKey) + '').toLowerCase();
        //         const email = (getJsonValue(item, itemEmailKey) + '').toLowerCase();
        //         // const mob = (getJsonValue(item, itemMobileKey) + '').toLowerCase();
        //
        //         let matched = false;
        //         // matched = (name + email).includes(inputValueL);
        //         matched = (name).includes(inputValueL);
        //
        //         // switch (searchByOption) {
        //         //     case SEARCH_BY_PARAMS.anything.name:
        //         //         matched = (name + email + mob + date).includes(inputValueL);
        //         //         break;
        //         //     case SEARCH_BY_PARAMS.title.name:
        //         //         matched = name.includes(inputValueL);
        //         //         break;
        //         //     case SEARCH_BY_PARAMS.desc.name:
        //         //         matched = email.includes(inputValueL);
        //         //         break;
        //         //     case SEARCH_BY_PARAMS.author.name:
        //         //         matched = mob.includes(inputValueL);
        //         //         break;
        //         //     case SEARCH_BY_PARAMS.date.name:
        //         //         matched = date.includes(inputValueL);
        //         //         break;
        //         // }
        //         return {
        //             ...item,
        //             searched: matched
        //         }
        //     }));
        // });

        // };
    }, [startSearchTransition]);

    const handleItemSelect = useCallback((currItem) => {
        startItemSelectTransition(() => {
            const updatedIds = new Set(selectedUserIds);
            if (updatedIds.has(currItem[M_ID])) {
                updatedIds.delete(currItem[M_ID]);
            } else {
                updatedIds.add(currItem[M_ID]);
            }
            setSelectedUserIds(updatedIds);

            sendItemSelectionChangeCallback(userList, updatedIds);
        });
    }, [selectedUserIds, startItemSelectTransition]);

    return (<div className={'position-relative bg-dangerx'}>
        {/*{reRenderTest()}*/}
        {/*header*/}
        {(isBoolTrue(showSearch) || isBoolTrue(showCounter) || isBoolTrue(showSelectAll)) && <Flexbox
            position={''} className={'w-100x'}
            justifyAt={'start'} alignAt={'center'}
            justifyAtSm={'start'} alignAtSm={'start'}
            wrapSm={'wrap'} directionSm={'column'}>
            {/*search & counter*/}
            <Flexbox className={''} justifyAt={'start'} alignAt={'center'}>
                {/*input-box*/}
                {isBoolTrue(showSearch) &&
                    <div className={`m-0 p-0 ms-2 position-relative ${!checkNullArr(userList) ? 'disable-item' : ''}`}>
                        {/*search-input*/}
                        <Input
                            wrapperClassName={`w-auto position-relative`}
                            innerWrapperClassName={'w-100x position-relative'}
                            className={`w-100 rounded-3x xpe-11`}
                            style={{height: '28px', backgroundColor: inputBg}}
                            placeholder={`Search... `}
                            showLabel={false}
                            leftIcon={`fa fa-search position-absolute`}
                            leftIconStyle={{left: '.1rem', top: '.4rem'}}
                            value={searchInputValue}
                            showMsg={false}
                            onChange={handleSearchChange}
                        />

                        {/*end - loader & search-by dropdown*/}
                        <Flexbox className={''} justifyAt={'end'} alignAt={'center'} position={'absolute'}
                                 style={{
                                     top: '.25rem', right: '.35rem',
                                 }}>

                            {/*search-result count*/}
                            {isSearchOngoing() &&
                                <Label
                                    className={`m-0 p-0 px-2 mx-1 text-nowrap fs-sm rounded-10 select-none fw-semi-bold d-flex al-center flex-nowrap jc-center`}
                                    tooltip={'Search Results'}
                                    color={'gray'}
                                    bgColor={'lightgray'}
                                    height={'20px'}>
                                    {getArrLen(userList.filter(item => isJsonValueTrue(item, 'searched')))}
                                </Label>}

                            {/*search-by dropdown*/}
                            {/*<ThemedDropdownMenu*/}
                            {/*    className={''}*/}
                            {/*    buttonClassName={'border-none fw-semi-bold rounded-10 px-1 text-nowrap'}*/}
                            {/*    buttonHeight={'22px'}*/}
                            {/*    outlined={false}*/}
                            {/*    buttonStyle={{*/}
                            {/*        // top: '.38rem', right: '.25rem',*/}
                            {/*    }}*/}
                            {/*    textPrefix={<span className={'fw-bold'}>With:</span>}*/}
                            {/*    defOption={getField(Fields.searchBySelectedOption)}*/}
                            {/*    onItemClick={(name, index) => {*/}
                            {/*        updateField(Fields.searchBySelectedOption, name);*/}
                            {/*        handleSearchInputChange(searchInputValue, name);*/}
                            {/*        // log('ThemedDropdownsMenu.onItemClick.name:', name);*/}
                            {/*    }}>{Object.values(SEARCH_BY_PARAMS)}</ThemedDropdownMenu>*/}
                        </Flexbox>
                    </div>}

                {/*counter*/}
                {isBoolTrue(showCounter) && <Flexbox className={'px-1 mt-n3 ms-2'}>
                    <Label className={'m-0 p-0'}>
                    <span className={'text-input-label'}>
                        {`${selectedUserIds.size}/${getArrLen(userList)}`}
                    </span>
                    </Label>
                </Flexbox>}
            </Flexbox>

            {/*select*/}
            {(isBoolTrue(showSelectAll)) && <div className={'mt-n3'}>
                <Flexbox justifyAt={'start'} alignAt={'center'}>
                    {/*select-all*/}
                    <div className={'w-autox px-2'}>
                        <Checkbox
                            wrapperClassName={'d-flexx w-autox'}
                            py={0}
                            px={1}
                            defaultChecked={getArrLen(isSearchOngoing() ? userList.filter(item => isJsonValueTrue(item, 'searched')) : userList) == selectedUserIds.size}
                            checked={getArrLen(isSearchOngoing() ? userList.filter(item => isJsonValueTrue(item, 'searched')) : userList) == selectedUserIds.size}
                            onChange={(e, value) => handleSelectAllItems(value)}>
                            Select All
                        </Checkbox>
                    </div>

                    {/*show selected-only*/}
                    {/*<div className={'w-auto'}>*/}
                    {/*    <Checkbox*/}
                    {/*        wrapperClassName={'d-flex w-auto'}*/}
                    {/*        // defaultChecked={getArrLen(userList) == selectedUserIds.size}*/}
                    {/*        // checked={getArrLen(userList) == selectedUserIds.size}*/}
                    {/*        onChange={(e, value) => handleShowSelectedItems(value)}>*/}
                    {/*        Show Selected*/}
                    {/*    </Checkbox>*/}
                    {/*</div>*/}
                </Flexbox>
            </div>}

            {/*loader*/}
            {(searchPending || itemSelectPending) && <CircleLoading wrapperClassName={'mt-n3 w-auto ms-2'} size={'14px'} borderWidth={'2px'}/>}
        </Flexbox>}


        {(dataLoadingPending || isBoolTrue(showLoading)) && <CircleLoading wrapperClassName={'p-3'} label={'Loading data...'}/>}

        {!dataLoadingPending && <div className={`m-0 p-0 bg-warningx scroller scroller-y-auto scroll-${scrollVariant} ${scrollerClassName}`}
                                     ref={wrapperRef}
                                     style={{minHeight: minHeight, maxHeight: height}}>
            <GridBox className={`px-2 ${gridClassName}`}
                     sm={itemSm} md={itemMd} gap={0}>
                {checkNullArr(userList) && userList.map((item, index) => {
                    return (isSearchOngoing() ? isJsonValueTrue(item, 'searched') : !isSearchOngoing()) &&
                        <div key={item[M_ID] + 'mm' + item[itemNameKey] + index}
                             className={`p-1x pt-0 pb-1 px-2`}>
                            <UserItem
                                className={`${itemClassName}`}
                                // selected={isBoolTrue(item.selected)}
                                selected={selectedUserIds.has(item[M_ID])}
                                onClick={() => handleItemSelect(item)}
                                bg={itemBg}
                                selectedBg={itemSelectedBg}
                                hoverBg={itemHoverBg}
                                selectedHoverBg={itemSelectedHoverBg}
                            >
                                {isBoolTrue(showImage) && <Avatar
                                    wrapperClassName={'mb-n1'}
                                    bordered={false}
                                    showInitials={isBoolTrue(showNameInitials)}
                                    nameForInitials={item[itemNameKey]}
                                    size={imageSize}>
                                    {item[itemImageKey]}
                                </Avatar>}

                                {/*text-content*/}
                                <div className={'m-0 p-0 overflow-hidden ms-2'}>
                                    {/*name*/}
                                    <div className={'bg-infox d-flex fd-row jc-start al-center'}>
                                        <Label
                                            className={`m-0 p-0 text-nowrap x fw-semi-bold cursor-pointer ${nameClassName}`}
                                            color={selectedUserIds.has(item[M_ID]) ? nameSelectedColor : nameColor}
                                            textAt={'start'}>
                                            {/*name*/}
                                            <span>{highlightSearchMatchedText(item[itemNameKey])}</span>

                                            {/*email*/}
                                            {(isBoolTrue(inlineItem) && (isBoolTrue(showEmail) || isBoolTrue(showMobile))) &&
                                                <span className={`m-0 p-0 text-start text-nowrap fs-smx text-ellipsis cursor-pointer ${emailClassName}`}
                                                      style={{
                                                          color: selectedUserIds.has(item[M_ID]) ? emailSelectedColor : emailColor
                                                      }}>
                                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                                    {isBoolTrue(showEmail) && <span>{highlightSearchMatchedText(item[itemEmailKey])}</span>}
                                                    {isBoolTrue(showMobile) && <span>&nbsp;&nbsp;
                                                        <span>{highlightSearchMatchedText(item[itemMobileKey])}</span>
                                                    </span>}
                                                </span>}
                                        </Label>
                                    </div>

                                    {/*email/mobile*/}
                                    {(!isBoolTrue(inlineItem) && (isBoolTrue(showEmail) || isBoolTrue(showMobile))) &&
                                        <div className={'d-flex fd-row jc-start al-start flex-nowrap'}>
                                            {isBoolTrue(showEmail) && <Label
                                                className={`m-0 p-0 text-nowrap -smx text-ellipsis cursor-pointer ${emailClassName}`}
                                                textAt={'start'}
                                                color={selectedUserIds.has(item[M_ID]) ? emailSelectedColor : emailColor}>
                                                <span>{highlightSearchMatchedText(item[itemEmailKey])} </span>
                                            </Label>}

                                            {isBoolTrue(showMobile) && <Label
                                                className={`m-0 p-0 text-nowrap -smx text-ellipsis cursor-pointer ${isBoolTrue(showEmail) && checkNullStr(item[itemEmailKey]) ? `ms-2` : ``} ${mobileClassName}`}
                                                textAt={'start'}
                                                color={selectedUserIds.has(item[M_ID]) ? mobileSelectedColor : mobileColor}>
                                                <span>{highlightSearchMatchedText(item[itemMobileKey])} </span>
                                            </Label>}
                                        </div>}
                                </div>
                            </UserItem>
                        </div>
                })}
            </GridBox>
            {checkNullStr(listMsg) &&
                <NullMsgLabel className={'ms-n1'} color={msgLabelColor}>{listMsg}</NullMsgLabel>}
        </div>}
    </div>);

    function handleSelectAllItems(selected) {
        const fun = 'handleSelectAllItems:';
        try {
            // const alreadyIds = selectedUserIds;
            // log(fun, 'selected:', selected, 'userList.len:', getArrLen(getUserListOrg()), 'alreadyIds.size', alreadyIds.size);
            // if (isSearchOngoing() ? (checkNullArr(userList) && userList.filter(item => isJsonValueTrue(item, 'searched'))) : checkNullArr(userList)) {
            if (checkNullArr(userList)) {
                startItemSelectTransition(() => {
                    const updatedIds = new Set([]);
                    // (isSearchOngoing() ? userList.filter(item => isJsonValueTrue(item, 'searched')) : userList).map((item, index) => {
                    userList.map((item, index) => {
                        // if (isSearchOngoing()) {
                        //     if (alreadyIds.has(item[M_ID])) {
                        //         log(fun, 'isSearchOngoing.alreadyIds.has-true, selected:', selected);
                        //         if (selected) updatedIds.add(item[M_ID]);
                        //         else updatedIds.delete(item[M_ID]);
                        //     } else {
                        //         log(fun, 'isSearchOngoing.alreadyIds.has-false, selected:', selected);
                        //         if (selected) updatedIds.add(item[M_ID]);
                        //         else updatedIds.delete(item[M_ID]);
                        //     }
                        // } else {
                        //     if (selected) updatedIds.add(item[M_ID]);
                        //     else updatedIds.delete(item[M_ID]);
                        // }
                        if (selected) updatedIds.add(item[M_ID]);
                        else updatedIds.delete(item[M_ID]);
                    });
                    // log(fun, 'updatedIds:', updatedIds);
                    // log(fun, 'selected:', selected, 'userList.len:', getArrLen(getUserListOrg()), 'updatedIds.size', updatedIds.size);
                    setSelectedUserIds(updatedIds);
                    sendItemSelectionChangeCallback(userList, updatedIds);
                });
            }
        } catch (error) {
            logErr(fun, error);
        }
    }

    function sendItemSelectionChangeCallback(userList, updatedIds) {
        const fun = 'sendItemSelectionChangeCallback:';
        try {
            if (onUsersSelected) {
                const updatedList = userList.map((item, index) => {
                    const itemCopy = {...item}; // Create a copy of the original item
                    const selected = !updatedIds.has(itemCopy[M_ID]);
                    deleteJsonKey(itemCopy, M_ID);
                    deleteJsonKey(itemCopy, 'searched');
                    return {
                        ...itemCopy,
                        selected: selected,
                    }
                });
                // const selectedList = updatedList.filter(item => isJsonValueTrue(item, 'selected'));
                onUsersSelected(updatedList, updatedList.filter(item => !isJsonValueTrue(item, 'selected')));
            }
        } catch (error) {
            logErr(fun, error);
        }
    }

    function getUserListOrg() {
        return checkNullArr(users) ? users : [];
    }

    function isSearchOngoing() {
        return checkNullStr(searchInputValue)
    }

    function highlightSearchMatchedText(text) {
        return isSearchOngoing() ? getHighlightedMatchedText(text, searchInputValue) : text;
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});

export default SearchableUserGridList;
